import { MainPropertyType, XMLPropertyResult } from '@aloft/toolkit-shared';

import { extractAdditionalFeatures } from './extractAdditionalFeatures.js';
import { extractAge } from './extractAge.js';
import { extractBasement } from './extractBasement.js';
import { extractCarStorage } from './extractCarStorage.js';
import { extractConcessions } from './extractConcessions.js';
import { extractCondition } from './extractCondition.js';
import { extractDataSourceDescription } from './extractDataSourceDescription.js';
import { extractDateOfSale } from './extractDateOfSale.js';
import { extractGLA } from './extractGLA.js';
import { extractLocation } from './extractLocation.js';
import { extractLocationTag } from './extractLocationTag.js';
import { extractNumberOfUnits } from './extractNumberOfUnits.js';
import { extractPorchPatioDeck } from './extractPorchPatioDeck.js';
import { extractPrice } from './extractPrice.js';
import { extractPropertyType } from './extractPropertyType.js';
import { extractQuality } from './extractQuality.js';
import { extractRoomCounts } from './extractRoomCounts.js';
import { extractSiteArea } from './extractSiteArea.js';
import { extractTermsOfSale } from './extractTermsOfSale.js';
import { extractView } from './extractView.js';
import { AdditionalFeatureLabels, AdditionalFeatures } from './types.js';

export function extractPropertyResult(
  sale: Element,
  additionalFeatures:
    | { t: 'subject'; features: AdditionalFeatures }
    | { t: 'comparable'; labels: AdditionalFeatureLabels },
  defaultPropertyType: MainPropertyType,
): XMLPropertyResult {
  const propertyType = extractPropertyType(sale, defaultPropertyType);
  const { address, latitude, longitude } = extractLocationTag(sale);
  const { mls, mlsListingID, daysOnMarket } = extractDataSourceDescription(sale);
  const price = extractPrice(sale);
  const age = extractAge(sale);
  const { additionalFeature1, additionalFeature2, additionalFeature3 } =
    additionalFeatures.t === 'subject'
      ? {
          additionalFeature1: additionalFeatures.features.feature1,
          additionalFeature2: additionalFeatures.features.feature2,
          additionalFeature3: additionalFeatures.features.feature3,
        }
      : extractAdditionalFeatures(sale, {
          featureLabel1: additionalFeatures.labels.featureLabel1,
          featureLabel2: additionalFeatures.labels.featureLabel2,
          featureLabel3: additionalFeatures.labels.featureLabel3,
        });

  const numberOfUnits = propertyType === 'multiFamily' ? extractNumberOfUnits(sale) : undefined;
  const gla = extractGLA(sale);
  const { fullBaths, halfBaths } = extractRoomCounts(sale);
  const basement = extractBasement(sale);
  const { garageSpaces, carportSpaces } = extractCarStorage(sale);
  const { status, contractDate, saleDate } = extractDateOfSale(sale);
  const siteArea = extractSiteArea(sale);
  const location = extractLocation(sale);
  const view = extractView(sale);
  const concessions = extractConcessions(sale);
  const termsOfSale = extractTermsOfSale(sale);
  const quality = extractQuality(sale);
  const condition = extractCondition(sale);
  const porchPatioDeck = extractPorchPatioDeck(sale);

  return {
    address,
    age,
    gla,
    fullBaths,
    halfBaths,
    basement,
    garageSpaces,
    carportSpaces,
    numberOfUnits,
    status,
    price,
    saleDate,
    contractDate,
    daysOnMarket,
    latitude,
    longitude,
    mls,
    mlsListingID,
    siteArea,
    location,
    view,
    quality,
    condition,
    concessions,
    termsOfSale,
    propertyType,
    porchPatioDeck,
    additionalFeature1,
    additionalFeature2,
    additionalFeature3,
  };
}
